/**
 * Transaction Service
 * Business logic for transaction operations
 */

import { 
  API_ENDPOINTS, 
  makeAuthenticatedApiRequest, 
  type ApiResult, 
  type GetTransactionsResponse,
  type CreateTransactionResponse,
  type UpdateTransactionResponse,
  type DeleteTransactionResponse,
  type TransactionParseRequest,
  type TransactionParseResponse
} from './apiConfig';
import { validateTransaction, validateAccessToken, validateMonth, validateTransactionId, validateTransactionParseRequest, validateUpdateTransactionRequest, type Transaction, type UpdateTransactionRequest } from './validators';

// Default pagination limit for transaction requests
const DEFAULT_TRANSACTION_LIMIT = 100;

/**
 * Create a new transaction via API
 * Note: Category will be normalized to lowercase for consistency
 * If transactionDate is provided, it will be sent to the API in ISO 8601 format
 * If not provided, the API will use the current timestamp
 */
export async function createTransaction(
  transaction: Transaction,
  accessToken: string
): Promise<ApiResult<CreateTransactionResponse>> {
  // Input validation
  validateTransaction(transaction);
  validateAccessToken(accessToken);
  
  const payload: any = {
    amount: transaction.amount,
    type: transaction.type,
    description: transaction.description,
    category: transaction.category.toLowerCase() // Normalized to lowercase
  };
  
  // Include transactionDate if provided
  if (transaction.date) {
    // Ensure it's in ISO 8601 format
    const date = new Date(transaction.date);
    payload.transactionDate = date.toISOString();
  }
  
  // Include goal if provided (for savings transactions)
  if (transaction.goal) {
    payload.goal = transaction.goal;
  }
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.TRANSACTIONS.CREATE, payload, accessToken);
}

/**
 * Get transactions for a specific month via API
 */
export async function getTransactions(
  month: string,
  accessToken: string,
  limit: number = DEFAULT_TRANSACTION_LIMIT
): Promise<ApiResult<GetTransactionsResponse>> {
  // Input validation
  validateMonth(month);
  validateAccessToken(accessToken);
  
  const url = new URL(API_ENDPOINTS.TRANSACTIONS.GET.url);
  url.searchParams.append('month', month);
  if (limit !== DEFAULT_TRANSACTION_LIMIT) {
    url.searchParams.append('limit', limit.toString());
  }
  
  const endpoint = {
    ...API_ENDPOINTS.TRANSACTIONS.GET,
    url: url.toString()
  };
  
  return makeAuthenticatedApiRequest(endpoint, undefined, accessToken);
}

/**
 * Update an existing transaction via API
 * Transaction ID should be in format: TXN#YYYY-MM#ISO8601#timestamp
 * All fields are optional - only provided fields are updated
 * Note: Category will be normalized to lowercase for consistency
 * Note: Goal updates are omitted as per requirements
 */
export async function updateTransaction(
  transactionId: string,
  updates: UpdateTransactionRequest,
  accessToken: string
): Promise<ApiResult<UpdateTransactionResponse>> {
  // Input validation
  validateTransactionId(transactionId);
  validateUpdateTransactionRequest(updates);
  validateAccessToken(accessToken);
  
  // Create payload with only provided fields
  const payload: any = {};
  
  if (updates.amount !== undefined) {
    payload.amount = updates.amount;
  }
  
  if (updates.type !== undefined) {
    payload.type = updates.type;
  }
  
  if (updates.description !== undefined) {
    payload.description = updates.description;
  }
  
  if (updates.category !== undefined) {
    payload.category = updates.category.toLowerCase(); // Normalized to lowercase
  }
  
  // Omit goal updates as per requirements
  
  const endpoint = API_ENDPOINTS.TRANSACTIONS.UPDATE(transactionId);
  
  return makeAuthenticatedApiRequest(endpoint, payload, accessToken);
}

/**
 * Delete a transaction via API
 * Transaction ID should be in format: TXN#YYYY-MM#ISO8601#timestamp
 */
export async function deleteTransaction(
  transactionId: string,
  accessToken: string
): Promise<ApiResult<DeleteTransactionResponse>> {
  // Input validation
  validateTransactionId(transactionId);
  validateAccessToken(accessToken);
  
  const endpoint = {
    ...API_ENDPOINTS.TRANSACTIONS.DELETE,
    url: `${API_ENDPOINTS.TRANSACTIONS.DELETE.url}/${encodeURIComponent(transactionId)}`
  };
  
  return makeAuthenticatedApiRequest(endpoint, undefined, accessToken);
}

/**
 * Parse natural language transaction description into structured transactions via API
 * Uses AI to extract individual transactions with amounts, types, descriptions, categories, and dates
 * @param description - Natural language description of transactions
 * @param accessToken - JWT access token for authentication
 * @param referenceDate - Optional ISO 8601 formatted reference date to help interpret relative dates (e.g., "yesterday", "today")
 */
export async function parseTransactionDescription(
  description: string,
  accessToken: string,
  referenceDate?: string
): Promise<ApiResult<TransactionParseResponse>> {
  // Input validation
  validateTransactionParseRequest(description, referenceDate);
  validateAccessToken(accessToken);
  
  const payload: TransactionParseRequest = {
    description: description.trim()
  };
  
  // Include referenceDate if provided
  if (referenceDate) {
    payload.referenceDate = referenceDate;
  }
  
  return makeAuthenticatedApiRequest(API_ENDPOINTS.TRANSACTIONS.PARSE, payload, accessToken);
}